import axios, { AxiosError, AxiosResponse } from 'axios'
import { useAuthStore } from '@/store/auth'
import toast from 'react-hot-toast'

// API Base Configuration
// Vite import.meta.env typing
interface ImportMetaEnv {
  readonly VITE_API_BASE_URL?: string
}
declare const importMeta: { env: ImportMetaEnv }
const VITE_ENV = (import.meta as unknown as typeof importMeta).env
const API_BASE_URL = ''

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const { tokens } = useAuthStore.getState()
    
    if (tokens?.access_token) {
      config.headers.Authorization = `Bearer ${tokens.access_token}`
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const { logout, tokens, setTokens } = useAuthStore.getState()
    
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || ''
      // Skip global handling for login endpoint; let UI manage the error gracefully
      if (requestUrl.includes('/api/v1/auth/login')) {
        return Promise.reject(error)
      }
      // Try to refresh token if available
      if (tokens?.refresh_token) {
        try {
          const refreshResponse = await axios.post(
            `${API_BASE_URL}/api/v1/auth/refresh`,
            { refresh_token: tokens.refresh_token }
          )
          
          const newTokens = refreshResponse.data
          setTokens(newTokens)
          
          // Retry original request with new token
          if (error.config) {
            error.config.headers.Authorization = `Bearer ${newTokens.access_token}`
            return apiClient.request(error.config)
          }
        } catch (refreshError) {
          // Refresh failed, logout user
          logout()
          toast.error('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.')
          if (window.location.pathname !== '/auth/login') {
          window.location.href = '/auth/login'
          }
        }
      } else {
        // No refresh token, logout user
        logout()
        toast.error('Autorisierungsfehler. Bitte melden Sie sich erneut an.')
        if (window.location.pathname !== '/auth/login') {
        window.location.href = '/auth/login'
        }
      }
    }
    
    // Handle other HTTP errors
    if (error.response?.status === 403) {
      toast.error('Sie haben keine Berechtigung für diese Aktion.')
    } else if (error.response?.status === 404) {
      toast.error('Angeforderte Ressource nicht gefunden.')
    } else if (error.response?.status === 422) {
      const data: any = error.response?.data || {}
      const validationErrors = data?.detail
      if (Array.isArray(validationErrors)) {
        validationErrors.forEach((err: any) => {
          toast.error(err?.msg || 'Ungültige Daten.')
        })
      } else {
        toast.error('Ungültige Daten gesendet.')
      }
    } else if ((error.response?.status ?? 0) >= 500) {
      toast.error('Serverfehler. Bitte versuchen Sie es später erneut.')
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Anfrage ist abgelaufen.')
    } else if (!error.response) {
      toast.error('Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung.')
    }
    
    return Promise.reject(error)
  }
)

// API Response Types
export interface ApiResponse<T> {
  data: T
  message?: string
  success: boolean
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
  has_next: boolean
  has_prev: boolean
}

export interface ApiError {
  detail: string | Array<{
    loc: string[]
    msg: string
    type: string
  }>
}

// Generic API request helper
export const apiRequest = async <T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  data?: any,
  config?: any
): Promise<T> => {
  try {
    const response = await apiClient.request<T>({
      method,
      url,
      data,
      ...config,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

// Convenience methods
export const get = <T>(url: string, config?: any): Promise<T> =>
  apiRequest<T>('GET', url, undefined, config)

export const post = <T>(url: string, data?: any, config?: any): Promise<T> =>
  apiRequest<T>('POST', url, data, config)

export const put = <T>(url: string, data?: any, config?: any): Promise<T> =>
  apiRequest<T>('PUT', url, data, config)

export const del = <T>(url: string, config?: any): Promise<T> =>
  apiRequest<T>('DELETE', url, undefined, config)

export default apiClient 